// src/features/consultation/useAllergyStatus.ts
// The three-valued allergy verdict — extracted from ConsultStage.tsx (where
// it originated) so PatientBanner can show the same signal everywhere the
// banner appears, not just at Consultation. "Not reviewed" (warn) is
// deliberately distinct from "reviewed, none found" (safe): nobody having
// asked is not the same as there being nothing to find, and a prescribing
// screen needs to tell the two apart.
import { useAllergies } from './useAllergies';

/** The sentinel row substance meaning "asked, and the answer was none" —
 * see patientAllergies in api.ts's own header comment: "No known allergies"
 * is a row, not an empty list. */
export const NO_KNOWN_ALLERGIES = 'No known allergies';

export type AllergyStatus = 'danger' | 'warn' | 'safe' | null;

/** null while loading/erroring/no patient yet. */
export function useAllergyStatus(uhid: string): AllergyStatus {
  return useAllergyDigest(uhid).status;
}

export interface AllergyDigest {
  status: AllergyStatus;
  /** The actual substances on record, sentinel excluded — so a badge can say
   * "Allergy: Penicillin" rather than "Allergy on record". Empty unless
   * `status` is 'danger'. */
  substances: string[];
}

/** The same verdict, plus the substances behind it. Naming the allergen is
 * the whole point of surfacing it in a banner a prescriber reads — "on
 * record" tells them to go and look something up, which is the click this
 * badge exists to save. */
export function useAllergyDigest(uhid: string): AllergyDigest {
  const query = useAllergies(uhid);
  if (!uhid || query.isLoading || query.isError) return { status: null, substances: [] };
  const allergies = query.data ?? [];
  const real = allergies.filter(a => a.substance !== NO_KNOWN_ALLERGIES);
  if (real.length) return { status: 'danger', substances: real.map(a => a.substance) };
  if (allergies.length) return { status: 'safe', substances: [] };
  return { status: 'warn', substances: [] };
}
